import React, { useContext, useMemo, useState } from 'react';
import { OptionsSkeleton } from 'src/elements/Skeletons/Skeletons';
import { Skeleton } from 'src/elements/Skeletons/Skeletons';
import { isVatEnabledForCategoryType } from 'src/RootComponents/Product/ProductRoot';
import { TieredPricing, TierPrice } from 'src/components/ProductDetail/TieredPricing';
import { ProductVariantSkeleton } from 'src/elements/Skeletons/Skeletons';

import { LazyLoader } from '../Helpers/LazyLoader';

import { ProductOptionsContext } from './ProductOptionsProvider';
import { SwatchData } from './utils/swatchData';
import {
    BaseVariant,
    CamelizedCategory,
    MetadataAttributeCodes,
    Product,
    ProductTagsLabels,
    ProductVariant,
} from './utils/product-detail.types';
import { ProductSwatchConnected } from './ProductSwatch';
import { InitialProps, ProductDetailProvider } from './ProductDetailContext';
import { ProductActionsLoaderProps } from './ProductActionsLoader';
import { useVariantQuery } from './VariantQuery';
import { ProductDetailPricing } from './ProductDetailPricing';

export interface ProductConfigOptionsValues {
    label: string;
    default_label: string;
    store_label: string;
    use_default_value: boolean;
    value_index: number;
    swatch_data?: SwatchData;
}

export interface ProductConfigOptions {
    attribute_code: string;
    attribute_id: string;
    id: number;
    label: string;
    position: number;
    values: ProductConfigOptionsValues[];
}

export type ShippingGroups = {
    sku: string;
    shipperhq_shipping_group: string;
}[];

const categoriesWithBulkOrderBlock = [
    'bags',
    'headwear/beanies',
    'headwear/caps',
    'footwear',
    'hi-vis',
    'hoodies',
    'jackets',
    'polo-shirts',
    'ppe',
    'sweatshirts',
    'trousers',
    't-shirts',
];

const overrideBulkOrdersNames = [{ category: 'bags', name: 'Bag' }];

export const getCategoryDetails = (categories: CamelizedCategory[]) => {
    const validCategory = categories.find((category) => categoriesWithBulkOrderBlock.includes(category.urlPath));

    if (validCategory) {
        const formattedCategoryName: string = validCategory.name
            .split(' ')
            .map((namePart) => namePart.charAt(0).toUpperCase() + namePart.slice(1))
            .join('-');

        const formattedUrl: string = validCategory.urlPath.includes('/')
            ? validCategory.urlPath.split('/')[1]
            : validCategory.urlPath;

        const isOverridedElement = overrideBulkOrdersNames.find((value) => value.category === validCategory.urlPath);

        if (isOverridedElement) {
            return {
                url: formattedUrl,
                name: isOverridedElement.name,
            };
        }

        return {
            url: formattedUrl,
            name:
                !formattedCategoryName.includes('Hi-Vis') && !formattedCategoryName.includes('Trousers')
                    ? formattedCategoryName.split('s').join('')
                    : formattedCategoryName,
        };
    }

    return undefined;
};

const LazyProductActionsLoader: React.FC<ProductActionsLoaderProps> = React.lazy(
    () => import(/* webpackChunkName:"__lazy__product-actions" */ './ProductActionsLoader'),
);

type MetaData = {
    customAttributeMetadata: {
        items: {
            attribute_options: {
                value: string;
                label: string;
            }[];
            attribute_code: string;
        }[];
    };
};

type ProductOptionsProps = {
    product: Product;
    isVatEnabledForCategory: isVatEnabledForCategoryType;
    priceChanged?: (price: TierPrice) => void;
    quantityChanged: (quantity: number) => void;
    selectCorrectShippingMethods: (sku: string, value: number) => void;
    simpleProductsShippingGroups?: ShippingGroups;
    metaData: MetaData;
    resetShippingGroups?: () => void;
};

type ProductVariantWrapperProps = {
    product: Product;
    isVatEnabledForCategory: isVatEnabledForCategoryType;
    priceChanged?: (price: TierPrice) => void;
    quantityChanged: (quantity: number) => void;
    selectCorrectShippingMethods: (sku: string, value: number) => void;
    simpleProductsShippingGroups?: ShippingGroups;
    metaData: MetaData;
    resetShippingGroups?: () => void;
};

export const ProductOptions: React.FC<ProductOptionsProps> = ({
    product,
    isVatEnabledForCategory,
    priceChanged,
    quantityChanged,
    selectCorrectShippingMethods,
    simpleProductsShippingGroups,
    metaData,
    resetShippingGroups,
}) => {
    const { loading, error } = useContext(ProductOptionsContext);

    if (loading) {
        return <OptionsSkeleton />;
    }

    if (error) {
        console.error(error);
        return <p>Couldn't load options</p>;
    }

    return (
        <ProductVariantWrapper
            product={product}
            isVatEnabledForCategory={isVatEnabledForCategory}
            priceChanged={priceChanged}
            quantityChanged={quantityChanged}
            selectCorrectShippingMethods={selectCorrectShippingMethods}
            simpleProductsShippingGroups={simpleProductsShippingGroups}
            metaData={metaData}
            resetShippingGroups={resetShippingGroups}
        />
    );
};

function variantsWithoutStock(baseVariants: BaseVariant[]): ProductVariant[] {
    return baseVariants.map((variant) => ({ ...variant, media_entries: [], small_image: '' }));
}

export const ProductVariantWrapper: React.FC<ProductVariantWrapperProps> = ({
    quantityChanged,
    priceChanged,
    product,
    isVatEnabledForCategory,
    selectCorrectShippingMethods,
    simpleProductsShippingGroups,
    metaData,
    resetShippingGroups,
}) => {
    const [localVat, setLocalVat] = useState(null);
    const {
        sku,
        name,
        margin_tier_prices,
        small_image,
        locations,
        price_estimates,
        size_guides,
        shippingType,
        default_colour_option,
        default_size,
        price_range,
    } = product;
    const { colors, baseVariants } = useContext(ProductOptionsContext);
    const { initialValues, hydratedVariants, computedSizes, loading, data, error } = useVariantQuery();
    const stockLoaded = Boolean(data && hydratedVariants.length > 0);
    const variants = useMemo(
        () => (stockLoaded ? hydratedVariants : variantsWithoutStock(baseVariants)),
        [stockLoaded, hydratedVariants, baseVariants],
    );
    const sizes = useMemo(
        () => (stockLoaded ? computedSizes : computedSizes.map((size) => ({ ...size, stock: undefined }))),
        [stockLoaded, computedSizes],
    );

    if (data?.productList.length === 0 || !initialValues || variants.length === 0 || computedSizes.length === 0) {
        return <ProductVariantSkeleton />;
    }

    const sectionIndex = isVatEnabledForCategory ? 1 : 0;

    const defaultColorOption = metaData?.customAttributeMetadata?.items?.[0].attribute_options.find(
        (option) => option.value === default_colour_option?.toString(),
    );

    const defaultSizeOption = metaData?.customAttributeMetadata?.items?.[1].attribute_options.find(
        (option) => option.value === default_size?.toString(),
    );

    const isPriceBreak = margin_tier_prices.length > 1;

    const productTags = product?.product_tags?.replace(/, +/g, ',').split(',').map(String);
    const productTagsMetadata = metaData?.customAttributeMetadata?.items.find(
        (item) => item.attribute_code === MetadataAttributeCodes.ProductTags,
    );

    const tagsLabels = productTagsMetadata?.attribute_options
        .filter((option) => productTags?.some((tag) => tag === option.value))
        .map((tag) => tag.label);

    const isCallUsTierEnabled = tagsLabels?.includes(ProductTagsLabels.CallUs);

    if (error) {
        console.error(error);
        return <p>Couldn't load options</p>;
    }

    return (
        <ProductDetailProvider
            price={{
                regular_price_incl: 0,
                regular_price: 0,
                final_price_incl: 0,
                final_price: 0,
                price: 0,
                price_incl: 0,
            }}
            variants={variants}
            parentSku={sku}
            parentName={name}
            parentImage={small_image}
            initialValues={initialValues as InitialProps}
            initialQty={0}
            tierPrices={margin_tier_prices}
            priceEstimates={price_estimates}
            localVat={localVat}
            quantityChanged={quantityChanged}
            price_range={price_range}
        >
            {isPriceBreak && (
                <TieredPricing
                    tieredPricing={margin_tier_prices}
                    priceChanged={priceChanged}
                    isCallUsTierEnabled={isCallUsTierEnabled}
                />
            )}

            <ProductSwatchConnected
                swatches={colors}
                count={colors.length}
                sizes={sizes}
                defaultColorPickerView={defaultColorOption?.label}
                defaultSizePickerView={defaultSizeOption?.label}
                sizeGuide={size_guides}
                loading={loading || !stockLoaded}
                currentVariants={variants}
                selectCorrectShippingMethods={selectCorrectShippingMethods}
                simpleProductsShippingGroups={simpleProductsShippingGroups}
                parentProductShippingGroup={product.shippingType}
                resetShippingGroups={resetShippingGroups}
            />

            {isVatEnabledForCategory && (
                <ProductDetailPricing title="Business Pricing" prices={margin_tier_prices} setLocalVat={setLocalVat} />
            )}
            <div>
                {stockLoaded ? (
                    <LazyLoader
                        loader={
                            <LazyProductActionsLoader
                                sectionIndex={sectionIndex}
                                canCustomise={locations.length > 0}
                                shippingType={shippingType}
                            />
                        }
                        fallback={<Skeleton />}
                    />
                ) : (
                    <Skeleton />
                )}
            </div>
        </ProductDetailProvider>
    );
};
